import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import { Box, Chip, IconButton, Typography } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { Menu, MenuItem, ProSidebar, SubMenu } from 'react-pro-sidebar';
import 'react-pro-sidebar/dist/css/styles.css';
import { NavLink, useLocation, matchPath } from 'react-router-dom';
import { useCookiesLogin } from '../../config/cookies';
import { useTranslation } from '../../hooks/useTranslation';
import { tokens } from '../../theme';
import LogoReduto from './../../assets/logo.png';

export function Logo() {
  return <img src={LogoReduto} alt="react logo" style={{ width: 150 }} />;
}

export const Item = ({ title, to, icon, submenuKey = null, setSelectedSubmenu, disabled = false }) => {
  const colors = tokens('dark');
  const location = useLocation();
  const isActive = !disabled && matchPath({ path: to, end: true }, location.pathname) !== null;

  useEffect(() => {
    if (isActive && submenuKey) {
      setSelectedSubmenu(submenuKey);
    } else if (isActive && !submenuKey) {
      setSelectedSubmenu(null);
    }
  }, [isActive, submenuKey, setSelectedSubmenu]);

  let itemStyle = {};
  let iconColor = colors.grey[100];

  if (disabled) {
    itemStyle = {
      color: colors.grey[600],
      backgroundColor: 'transparent',
      fontFamily: 'Ubuntu, sans-serif',
      cursor: 'not-allowed',
      opacity: 0.45,
      pointerEvents: 'none',
    };
    iconColor = colors.grey[600];
  } else if (isActive) {
    if (submenuKey) {
      itemStyle = {
        color: colors.greenAccent[500],
        backgroundColor: 'transparent',
        fontWeight: 500,
        fontFamily: 'Ubuntu, sans-serif',
      };
      iconColor = colors.greenAccent[500];
    } else {
      itemStyle = {
        color: '#000000',
        backgroundColor: colors.greenAccent[500],
        borderRadius: '4px',
        fontFamily: 'Ubuntu, sans-serif',
      };
      iconColor = '#000000';
    }
  } else {
    itemStyle = {
      color: colors.grey[100],
      backgroundColor: 'transparent',
      fontFamily: 'Ubuntu, sans-serif',
      fontWeight: submenuKey ? 500 : 400,
    };
  }

  const styledIcon = icon
    ? React.cloneElement(icon, { style: { color: iconColor } })
    : null;

  if (disabled) {
    return (
      <MenuItem style={itemStyle} icon={styledIcon}>
        <Typography style={{ fontWeight: submenuKey ? 500 : 400, fontFamily: 'Ubuntu, sans-serif', fontSize: '14px' }}>
          {title}
        </Typography>
      </MenuItem>
    );
  }

  return (
    <MenuItem active={isActive} style={itemStyle} icon={styledIcon}>
      <NavLink to={to} style={{ textDecoration: 'none', color: 'inherit' }}>
        <Typography style={{ fontWeight: submenuKey ? 500 : 400, fontFamily: 'Ubuntu, sans-serif', fontSize: '14px' }}>
          {title}
        </Typography>
      </NavLink>
    </MenuItem>
  );
};

export const CustomSubMenu = ({
  title,
  icon,
  children,
  selectedSubmenu,
  submenuKey,
  tag = null,
  tagColor = 'error',
  disabled = false,
}) => {
  const isActive = !disabled && selectedSubmenu === submenuKey;
  const colors = tokens('dark');

  return (
    <SubMenu
      title={
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
          <Typography sx={{ fontFamily: 'Ubuntu, sans-serif', fontSize: '14px', fontWeight: 500 }}>
            {title}
          </Typography>
          {tag && (
            <Chip
              label={tag}
              size="small"
              sx={{
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                height: 18,
                fontSize: '10px',
                fontWeight: 600,
              }}
              color={tagColor}
            />
          )}
        </Box>
      }
      icon={icon}
      className={isActive ? 'submenu-active' : ''}
      style={{
        fontFamily: 'Ubuntu, sans-serif',
        color: disabled ? colors.grey[600] : colors.grey[100],
        opacity: disabled ? 0.45 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
        cursor: disabled ? 'not-allowed' : 'pointer',
        marginBottom: '8px',
        fontSize: '14px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          marginTop: '6px',
        }}
      >
        {children}
      </Box>
    </SubMenu>
  );
};

const BaseSidebar = ({ children, includeHeight = false }) => {
  const colors = tokens('dark');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selectedSubmenu, setSelectedSubmenu] = useState(null);
  const { cookies } = useCookiesLogin();
  const { t } = useTranslation();

  const title = {
    super_admin: t('roles.admin'),
    admin: t('roles.admin'),
    consultant: t('roles.consultant'),
    user: t('roles.user'),
    representative: t('roles.representative'),
  };

  const response = cookies.AUTH_TOKEN || {};
  const titleFormat = title[response.role] || '';

  return (
    <Box
      sx={{
        '& .pro-sidebar.collapsed .pro-inner-item': {
          padding: '0 15px !important',
        },
        '& .pro-sidebar.collapsed .pro-arrow-wrapper': {
          display: 'none !important',
        },
        '& .pro-sidebar.collapsed .pro-inner-list-item .pro-item-content': {
          marginLeft: '-10px',
        },
        '& .pro-sidebar-inner': {
          background: `${colors.primary[400]} !important`,
        },
        '& .pro-icon-wrapper': {
          backgroundColor: 'transparent !important',
        },
        '& .pro-inner-item': {
          padding: '5px 35px 5px 20px !important',
          fontFamily: 'Ubuntu, sans-serif !important',
        },
        '& .pro-inner-item:hover': {
          color: `${colors.greenAccent[500]} !important`,
        },
        '& .pro-menu-item': {
          fontFamily: 'Ubuntu, sans-serif !important',
        },
        '& .pro-item-content': {
          fontFamily: 'Ubuntu, sans-serif !important',
        },

        // Estilizar apenas o título do submenu ativo
        '& .submenu-active > .pro-inner-item': {
          backgroundColor: `${colors.greenAccent[500]} !important`,
          borderRadius: '4px',
          color: '#000000 !important',
          fontFamily: 'Ubuntu, sans-serif !important',
        },
        '& .submenu-active > .pro-inner-item .pro-icon-wrapper': {
          color: '#000000 !important',
        },
        '& .submenu-active > .pro-inner-item .pro-icon': {
          color: '#000000 !important',
        },
        '& .submenu-active > .pro-inner-item svg': {
          color: '#000000 !important',
        },
        '& .submenu-active .pro-inner-list-item .pro-inner-item': {
          backgroundColor: 'transparent !important',
        },
        ...(includeHeight && { height: '100vh' }),
      }}
    >
      <ProSidebar collapsed={isCollapsed}>
        <Menu iconShape="square">
          {/* LOGO AND MENU ICON */}
          <MenuItem
            onClick={() => setIsCollapsed(!isCollapsed)}
            icon={
              isCollapsed ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width="100%"
                  marginLeft={2}
                >
                  <MenuOutlinedIcon />
                </Box>
              ) : undefined
            }
            style={{
              margin: '10px 0 20px 0',
              color: colors.grey[100],
              borderBottom: '1px solid white',
            }}
          >
            {!isCollapsed && (
              <Box display="flex" justifyContent="space-between" alignItems="center" ml="15px">
                <Logo />
                <IconButton onClick={() => setIsCollapsed(!isCollapsed)}>
                  <MenuOutlinedIcon style={{ color: '#fff' }} />
                </IconButton>
              </Box>
            )}
          </MenuItem>

          {/* USER INFO */}
          {!isCollapsed && (
            <Box mb="25px">
              <Box textAlign="center">
                <Typography
                  variant="h3"
                  color={colors.grey[100]}
                  fontWeight="bold"
                  sx={{ m: '10px 0 0 0' }}
                >
                  {response.name}
                </Typography>
                <Typography variant="h5" color={colors.greenAccent[500]}>
                  {titleFormat}
                </Typography>
              </Box>
            </Box>
          )}

          {/* MENU ITEMS */}
          <Box paddingX="5%">{children({ selectedSubmenu, setSelectedSubmenu })}</Box>
        </Menu>
      </ProSidebar>
    </Box>
  );
};

export default BaseSidebar;
